import d3 from 'd3';
import { Classes } from 'mbc/dist/mbc-core';
import 'mbc/dist/mbc-time-series-line.css';
import TimeSeriesLineChart from 'mbc/dist/mbc-time-series-line';
import LegendMixin from './legend-mixin';

const HypoLineChart = TimeSeriesLineChart.extend(LegendMixin, {
  updateTickWidths() {
    this.set('ticksUpdated', true);
    this.update({ noTicksRecalc: true });
  },

  isEmptyData() {
    const dataManager = this.get('data');
    const y1DomainDataset = this.get('y1DomainDataset');
    return dataManager.get(y1DomainDataset).length === 0;
  },

  beforeDraw() {
    this.__super__.beforeDraw.call(this);
    if (this.get('timelineLevel') === 'year') {
      this.set('_xTickFormat', d3.time.format('%Y'));
    }
  },

  // years don't show subLabels.
  appendAxisSubLabels() {
    if (this.get('timelineLevel') !== 'year') {
      this.__super__.appendAxisSubLabels.call(this);
    }
  },

  helperAddFirstLabel() {
    const canvas = this.get('canvas');
    const axes = canvas.get('axesGroup').select('.mbc-axis-x');
    const ticks = axes.selectAll('.tick').filter(function() {
      return this.getAttribute('class').indexOf('artificial') === -1;
    });
    let firstTick;
    if (this.get('timelineLevel') !== 'year') {
      this.__super__.helperAddFirstLabel.call(this, canvas);
      firstTick = axes.select('.tick.artificial.first')[0][0].childNodes[0];
    } else {
      firstTick = ticks[0][0].childNodes[1];
      ticks[0].shift();
    }
    this.handleTicksOverlap(ticks, firstTick);
  },

  handleTicksOverlap(ticks, firstTick) {
    const canvas = this.get('canvas');
    const maxBoundary = canvas.width() - this.get('yPadding');

    ticks.style('display', '');
    let tick1 = firstTick;
    let tick2;
    let tick1Rect;
    let tick2Rect;
    ticks.each(function() {
      tick2 = this.childNodes[1]; // childNode[1] is the top tick text element.
      tick1Rect = tick1.getBoundingClientRect();
      tick2Rect = tick2.getBoundingClientRect();
      // judge if the last tick exceeds the chart area.
      if (
        tick1Rect.x + tick1Rect.width > tick2Rect.x ||
        tick2Rect.x - firstTick.getBoundingClientRect().x + tick2Rect.width > maxBoundary
      ) {
        this.style.display = 'none';
      } else {
        tick1 = tick2;
      }
    });
  },

  draw: function draw() {
    this.buildScales();
    this.buildAxes();
    this.drawAxes();

    if (!this.isEmptyData()) {
      this.drawLines();
      this.drawBubbles();
      this.drawBackgroundBars();
    }

    this.drawGrid();
    this.drawClipPath();

    if (this.get('timelineLevel')) {
      this.helperSetSublabels();
    }
  },

  calculateDomain() {
    if (!this.isEmptyData()) {
      this.__super__.calculateDomain.call(this);
    } else {
      const y1tickValues = this.get('y1TickValues');
      this.set('y1Domain', [Math.min(y1tickValues), Math.max(y1tickValues)]);
    }
  },

  buildAxes() {
    this.__super__.buildAxes.call(this);
    const axes = this.get('axes');
    axes.remove('x');

    // rewrite x axis
    const scales = this.get('scales');
    const xScale = scales.scale('x');
    const xAxis = d3.svg
      .axis()
      .scale(xScale)
      .orient(this.get('xAxisOrient'))
      .tickSize(this.get('xTickSize'))
      .tickValues(this.get('xTickValues'))
      .tickFormat(this.get('_xTickFormat'));

    axes.axis('x', xAxis);
  },

  drawSVGLegend(params) {
    if (!this.isEmptyData()) {
      this.__super__.drawSVGLegend.call(this, params);
    }
  },

  invokeLegend(params) {
    const container = this.get('selection').selectAll(`.${Classes.LEGEND}`);
    if (container.classed(`${Classes.LEGEND}-right`)) {
      this.drawRightLegend();
    } else if (container.classed(`${Classes.LEGEND}-top`)) {
      this.__super__.invokeLegend.call(this, params);
    }
  },

  /* eslint-disable */
    //reference : 
    // http://www.cnblogs.com/pengcl/p/3278388.html
    //https://blog.csdn.net/u014170770/article/details/84878644    
    adjustYDomain(dataset, symmetrical) {
        const ticksNumber = this.get("ticksNumber") + 1;
        const extent = d3.extent(dataset);
        let min = extent[0];
        let max = extent[1];

        const step = this._getScaledYStep((max - min) / ticksNumber);

        if (parseInt(min / step) !== (min / step)) {
            if (min < 0) {
                min = (-1) * Math.ceil(Math.abs(min / step)) * step;
            } else {
                min = parseInt(Math.abs(min / step)) * step;
            }

        }
        if (parseInt(max / step) !== (max / step)) {
            if(max < 0) {
                max = (-1) * Math.floor(Math.abs(max / step)) * step;
            }else {
                max = parseInt(max / step + 1) * step;
            }
        }
        return [min, max];
    },

    _getScaledYStep(step) {
        let temp, tmpstep;

        if (Math.pow(10, parseInt(Math.log(step) / Math.log(10))) === step) {
            temp = Math.pow(10, parseInt(Math.log(step) / Math.log(10)));
        } else {
            temp = Math.pow(10, (parseInt(Math.log(step) / Math.log(10)) + 1));
        }

        tmpstep = step / temp;

        if (tmpstep >= 0 && tmpstep <= 0.1) {
            tmpstep = 0.1;
        } else if (tmpstep >= 0.100001 && tmpstep <= 0.2) {
            tmpstep = 0.2;
        } else if (tmpstep >= 0.200001 && tmpstep <= 0.25) {
            tmpstep = 0.25;
        } else if (tmpstep >= 0.250001 && tmpstep <= 0.5) {
            tmpstep = 0.5;
        } else {
            tmpstep = 1;
        }

        return tmpstep * temp;
    }
    /* eslint-enable */
});

export default HypoLineChart;
